/**
 * The price list beside the entrance (S42, § 13 Abs. 2 PAngV): one menu of the business, chosen in
 * its settings (0..1), whose prices are the catalogue's. The app remembers which state was printed
 * last and says when the paper at the door no longer shows what the catalogue says; it warns when
 * another menu asks a different price for a dish of the list, and points out (never blocks) dishes
 * of the list that the menu on the net does not have.
 */
import { digestOf, freezeMenu } from "./card-web.ts";
import type { Dish } from "./dishes.ts";
import { copyOf, drinksLike, menuDishes, priceDiffs, withCatalogPrices } from "./menu.ts";
import type { Menu, MenuInput, MenuView, PriceDiff } from "./menu.ts";

/** The title a new list at the entrance begins with (Martin, 28.10.2026). */
export const ENTRANCE_TITLE = "Auszug aus unserem Speisen- und Getränkeangebot";

/**
 * „Neuer Aushang als Auszug“: a copy of the menu with the title above, no subtitle, and one sheet for
 * the door – its drinks compact and on the same page, whatever look the drinks had last (B2,
 * Martin 02.11.2026; a new menu takes the drinks' look of the last one, `drinksLike`).
 */
export function entranceDraft(from: MenuInput, menus: readonly Pick<Menu, "createdAt" | "drinks">[], at: number): MenuInput {
  const copy = copyOf(from, at);
  return { ...copy, title: ENTRANCE_TITLE, subtitle: "", drinks: { ...(copy.drinks ?? drinksLike(menus)), layout: "compact", newPage: false } };
}

/** When the list at the entrance was printed last, and what it showed. */
export interface EntrancePrinted {
  menu: string;
  digest: string;
  at: number;
}

/** The list as it is printed: the menu with the catalogue's prices (a set menu keeps its own). */
export const entranceMenuOf = <M extends Pick<Menu, "sections">>(menu: M, dishes: ReadonlyMap<string, Dish>): M => withCatalogPrices(menu, dishes);

/** A mark of what a printed list shows: texts, prices, codes; another state, another mark. */
export const entranceDigest = (view: MenuView): string => digestOf({ ...freezeMenu("", view, "", 0), logo: null, logoOnly: false });

/** What another menu asks differently from the list at the entrance: dishes, and set menus of the same title. */
export interface EntranceConflicts {
  dishes: PriceDiff[];
  /** Set menus (S32) by their title: a guess, so only a warning. */
  sets: { title: string; entrance: number | null; here: number | null }[];
}

export function entranceConflicts(menu: Pick<MenuInput, "sections">, entrance: Pick<MenuInput, "sections">, dishes: ReadonlyMap<string, Dish>): EntranceConflicts {
  const onList = new Set(menuDishes(entrance));
  const key = (t: string) => t.normalize("NFC").toLocaleLowerCase("de-DE").replace(/\s+/g, " ").trim();
  const listed = new Map(entrance.sections.filter((s) => s.kind === "set" && s.title.trim()).map((s) => [key(s.title), s.price]));
  return {
    dishes: priceDiffs(menu, dishes).filter((d) => onList.has(d.dish)),
    sets: menu.sections.flatMap((s) => {
      if (s.kind !== "set" || !listed.has(key(s.title))) return [];
      const price = listed.get(key(s.title))!;
      return price !== s.price ? [{ title: s.title, entrance: price, here: s.price }] : [];
    }),
  };
}

export const hasConflicts = (c: EntranceConflicts) => c.dishes.length > 0 || c.sets.length > 0;

/** The dishes of the list that none of `menus` (the menu on the net, its drinks, the recommendations) has. */
export function missingFromNet(entrance: Pick<MenuInput, "sections">, menus: readonly Pick<MenuInput, "sections">[], dishes: ReadonlyMap<string, Dish>): Dish[] {
  const there = new Set(menus.flatMap(menuDishes));
  return [...new Set(menuDishes(entrance))].filter((id) => !there.has(id)).flatMap((id) => {
    const d = dishes.get(id);
    return d ? [d] : [];
  });
}
